/**
 * Working-days arithmetic — TS mirror of the BE `WorkingDays` + `HolidayCalendar` pair
 * (Soly.Execution.Domain/Planning), so a warning shown in the scheduling fiche computes the EXACT
 * same numbers as the Suivi alert behind it (_constraints.md « cohérence amont »). All functions
 * take/return ISO `yyyy-MM-dd` date strings (timezone-free wall dates, like the agenda grid).
 */

/** Easter Sunday via the anonymous Gregorian (Meeus/Jones/Butcher) algorithm — same as the BE. */
function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(Date.UTC(year, month - 1, day))
}

const iso = (d: Date): string => d.toISOString().slice(0, 10)
const addDays = (d: Date, n: number): Date => new Date(d.getTime() + n * 86_400_000)

const holidayCache = new Map<number, Set<string>>()

/** Belgian public holidays for a year (fixed dates + the moving feasts derived from Easter). */
export function belgianHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year)
  if (cached) return cached

  const easter = easterSunday(year)
  const pad = (n: number) => String(n).padStart(2, '0')
  const fixed = (month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`
  const set = new Set<string>([
    fixed(1, 1),            // Nouvel An
    iso(addDays(easter, 1)),  // Lundi de Pâques
    fixed(5, 1),            // Fête du Travail
    iso(addDays(easter, 39)), // Ascension
    iso(addDays(easter, 50)), // Lundi de Pentecôte
    fixed(7, 21),           // Fête nationale
    fixed(8, 15),           // Assomption
    fixed(11, 1),           // Toussaint
    fixed(11, 11),          // Armistice
    fixed(12, 25),          // Noël
  ])
  holidayCache.set(year, set)
  return set
}

/** A working day = a weekday that is not a Belgian public holiday (BE `WorkingDays.IsWorkingDay`). */
export function isWorkingDayIso(dateIso: string): boolean {
  const d = new Date(`${dateIso}T00:00:00Z`)
  const dow = d.getUTCDay()
  if (dow === 0 || dow === 6) return false
  return !belgianHolidays(d.getUTCFullYear()).has(dateIso)
}

/**
 * Working days in the half-open interval [start, end): the start day counts, the end day does not.
 * 0 when end is not after start — the exact BE `WorkingDays.Between` contract.
 */
export function workingDaysBetween(startIso: string, endIso: string): number {
  let count = 0
  let d = new Date(`${startIso}T00:00:00Z`)
  const end = new Date(`${endIso}T00:00:00Z`)
  while (d < end) {
    if (isWorkingDayIso(iso(d))) count += 1
    d = addDays(d, 1)
  }
  return count
}

/**
 * The M2/M3 gap (BE `AlertEngine.GapWorkingDays` convention, pinned in _constraints.md): working
 * days strictly BETWEEN the métré and the pose — from the first free day after the métré's last
 * day to the pose day (exclusive). Métré Monday, pose Friday → Tue+Wed+Thu = 3.
 */
export function surveyPoseGapWorkingDays(surveyLastDayIso: string, poseStartIso: string): number {
  const dayAfterSurvey = iso(addDays(new Date(`${surveyLastDayIso}T00:00:00Z`), 1))
  return workingDaysBetween(dayAfterSurvey, poseStartIso)
}

/**
 * The date N working days after `startIso` (exclusive of the start day). N=0 returns the start.
 * Used to prefill deadline inputs (e.g. confirmation expected = sent date + supplier delay).
 */
export function addWorkingDays(startIso: string, n: number): string {
  let d = new Date(`${startIso}T00:00:00Z`)
  let remaining = n
  while (remaining > 0) {
    d = addDays(d, 1)
    if (isWorkingDayIso(iso(d))) remaining -= 1
  }
  return iso(d)
}
